"use client";

import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createRendezvous,
  fetchPatientRendezvousByExternalId,
  fetchRendezvousByMedecin,
  cancelRendezvous,
  rescheduleRendezvous,
} from "../redux/thunks/rendezvousThunks";
import {
  selectActiveSubprofileId,
  selectEffectiveUserId,
} from "../../sous-profile/redux/selectors/sousProfileSelectors";

/* ── helpers ── */

const arr = (x) =>
  Array.isArray(x)
    ? x
    : Array.isArray(x?.data)
    ? x.data
    : Array.isArray(x?.items)
    ? x.items
    : Array.isArray(x?.rendezvous)
    ? x.rendezvous
    : [];

const pickPatientId = (auth) => {
  const u = auth?.currentUser || auth?.user || {};

  return (
    u.externalId ||
    u.uuid ||
    u.patientId ||
    u.pgId ||
    u._id ||
    u.id ||
    u.userId ||
    null
  );
};

const pickDoctorId = (auth) => {
  const u = auth?.currentUser || auth?.user || {};

  return (
    u.externalId ||
    u.uuid ||
    u.doctorId ||
    u.medecinId ||
    u.pgId ||
    u._id ||
    u.id ||
    u.userId ||
    null
  );
};

const normId = (v) => (v == null ? "" : String(v));

const cleanName = (value) => {
  const name = String(value || "").trim();

  if (!name) return "";

  const lower = name.toLowerCase();

  if (
    lower === "doctor" ||
    lower === "dr. doctor" ||
    lower === "dr doctor"
  ) {
    return "";
  }

  return name;
};

const toDisplayDates = (r) => {
  const st = r?.startTime ? new Date(r.startTime) : null;
  const et = r?.endTime ? new Date(r.endTime) : null;

  const dateText = st
    ? st.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "—";

  const fmt = (d) =>
    d
      ? d.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const timeText =
    st && et
      ? `Time: ${fmt(st)} - ${fmt(et)}`
      : st
      ? `Time: ${fmt(st)}`
      : "";

  return { dateText, timeText };
};

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const resolvePatientId = (authState, appointment, opt) =>
  opt ||
  appointment?.patientId ||
  appointment?.externalPatientId ||
  appointment?.userId ||
  pickPatientId(authState) ||
  null;

const resolveDoctorId = (authState, appointment, opt) =>
  opt ||
  appointment?.medecinId ||
  appointment?.doctorId ||
  pickDoctorId(authState) ||
  null;

/* ============================================================
   HOOK
============================================================ */

export default function useRendezvous() {
  const dispatch = useDispatch();

  const authState = useSelector((s) => s.auth);

  /*
   * IMPORTANT :
   * Le doctor connecté peut être stocké dans state.doctor
   * et non dans state.auth.
   */
  const doctorState = useSelector((s) => s.doctor);

  const effectivePatientId = useSelector(selectEffectiveUserId);
  const activeSubprofileId = useSelector(selectActiveSubprofileId);

  const creating = useSelector((s) => s.rendezvous?.creating);
  const createError = useSelector((s) => s.rendezvous?.createError);
  const lastCreated = useSelector((s) => s.rendezvous?.lastCreated);

  const rescheduling = useSelector(
    (s) => s.rendezvous?.rescheduling
  );

  const rescheduleError = useSelector(
    (s) => s.rendezvous?.rescheduleError
  );

  const inFlight = useRef(new Map());

  const once = useCallback(async (key, fn) => {
    const m = inFlight.current;

    if (m.has(key)) {
      return m.get(key);
    }

    const p = Promise.resolve()
      .then(fn)
      .finally(() => m.delete(key));

    m.set(key, p);

    return p;
  }, []);

  /* ============================================================
     CREATE
  ============================================================ */

  const bookRendezvous = useCallback(
    async ({
      medecinId,
      startTime,
      endTime,
      type = "standard",
      reason = null,
      ...rest
    }) => {
      const patientId = pickPatientId(authState);

      if (!patientId) {
        throw new Error(
          "You must be logged in as a patient."
        );
      }

      const subprofileId = activeSubprofileId || null;

      const payload = {
        startTime,
        endTime,
        type,
        reason:
          typeof reason === "string"
            ? reason.trim() || null
            : reason,
        ...rest,
        ...(subprofileId ? { subprofileId } : {}),
      };

      return dispatch(
        createRendezvous({
          patientId,
          medecinId,
          payload,
          asPatient: true,
        })
      ).unwrap();
    },
    [
      dispatch,
      authState,
      activeSubprofileId,
      effectivePatientId,
    ]
  );

  /* ============================================================
     CONFIRMED DOCTOR IDS FOR PATIENT
  ============================================================ */

  const getConfirmedDoctorIdsForPatient = useCallback(
    (patientIdOpt) => {
      const patientId =
        patientIdOpt ?? pickPatientId(authState);

      if (!patientId) {
        throw new Error("Patient id not found.");
      }

      return once(
        `confirmedDoctorIds:${patientId}`,
        async () => {
          const raw = await dispatch(
            fetchPatientRendezvousByExternalId({
              userId: patientId,
            })
          ).unwrap();

          const ids = new Set();

          for (const r of arr(raw)) {
            const status = String(
              r?.status || ""
            ).toLowerCase();

            if (
              status !== "confirmed" &&
              status !== "rescheduled"
            ) {
              continue;
            }

            const docId =
              r?.medecinId ??
              r?.doctorId;

            if (docId != null) {
              ids.add(normId(docId));
            }
          }

          return Array.from(ids);
        }
      );
    },
    [dispatch, authState, once]
  );

  /* ============================================================
     CONFIRMED PATIENT IDS FOR DOCTOR
  ============================================================ */

  const getConfirmedPatientIdsForDoctor = useCallback(
    async (doctorIdOpt) => {
      const authUser =
        authState?.currentUser ||
        authState?.user ||
        {};

      const doctorUser =
        doctorState?.user || {};

      /*
       * Priorité :
       *
       * 1. doctorId fourni directement
       * 2. state.doctor.user
       * 3. state.auth
       */
      const doctorId =
        doctorIdOpt ||
        doctorUser?.doctorId ||
        doctorUser?.medecinId ||
        doctorUser?.externalId ||
        doctorUser?.uuid ||
        doctorUser?.pgId ||
        doctorUser?._id ||
        doctorUser?.id ||
        doctorUser?.userId ||
        authUser?.doctorId ||
        authUser?.medecinId ||
        authUser?.externalId ||
        authUser?.uuid ||
        authUser?.pgId ||
        authUser?._id ||
        authUser?.id ||
        authUser?.userId ||
        null;

      if (!doctorId) {
        console.error(
          "[useRendezvous] Doctor id not found.",
          {
            doctorState,
            authState,
          }
        );

        throw new Error("Doctor id not found.");
      }

      const normalizedDoctorId =
        String(doctorId).trim();

      return once(
        `confirmedPatientIds:${normalizedDoctorId}`,
        async () => {
          const raw = await dispatch(
            fetchRendezvousByMedecin({
              userId: normalizedDoctorId,
              query: {},
            })
          ).unwrap();

          const ids = new Set();

          for (const r of arr(raw)) {
            const status = String(
              r?.status || ""
            ).toLowerCase();

            if (
              status !== "confirmed" &&
              status !== "rescheduled"
            ) {
              continue;
            }

            const patientId =
              r?.patientId ??
              r?.externalPatientId ??
              r?.userId;

            if (patientId != null) {
              const normalizedPatientId =
                String(patientId).trim();

              if (normalizedPatientId) {
                ids.add(normalizedPatientId);
              }
            }
          }

          return Array.from(ids);
        }
      );
    },
    [
      dispatch,
      authState,
      doctorState,
      once,
    ]
  );

  /* ============================================================
     FETCH PATIENT APPOINTMENTS
  ============================================================ */

  const _fetch = useCallback(
    async (patientId, mode) => {
      const raw = await dispatch(
        fetchPatientRendezvousByExternalId({
          userId: patientId,
        })
      ).unwrap();

      const now = Date.now();

      return arr(raw)
        .filter((r) => {
          const status = String(
            r?.status || ""
          ).toLowerCase();

          return (
            status === "confirmed" ||
            status === "rescheduled"
          );
        })
        .filter((r) => {
          const endTs = r?.endTime
            ? Date.parse(r.endTime)
            : NaN;

          const startTs = r?.startTime
            ? Date.parse(r.startTime)
            : NaN;

          const bound = Number.isNaN(endTs)
            ? startTs
            : endTs;

          if (Number.isNaN(bound)) {
            return false;
          }

          return mode === "upcoming"
            ? bound >= now
            : bound < now;
        })
        .sort((a, b) =>
          mode === "upcoming"
            ? Date.parse(
                a.startTime || a.endTime
              ) -
              Date.parse(
                b.startTime || b.endTime
              )
            : Date.parse(
                b.startTime || b.endTime
              ) -
              Date.parse(
                a.startTime || a.endTime
              )
        )
        .map((r) => {
          const {
            dateText,
            timeText,
          } = toDisplayDates(r);

          const doctorName = cleanName(
            r.medecinName ||
              r.doctorName ||
              r.providerName ||
              r.providerDisplay ||
              r.provider
          );

          return {
            id: r.id ?? r._id,

            status: r.status,

            startTime: r.startTime,
            endTime: r.endTime,

            doctor:
              r.doctor ??
              r.doctorInfo ??
              r.doctorData ??
              null,

            medecin:
              r.medecin ??
              r.medecinInfo ??
              r.medecinData ??
              null,

            provider:
              r.provider ?? null,

            medecinId:
              r.medecinId ??
              r.doctorId ??
              r.providerId,

            doctorId:
              r.doctorId ??
              r.medecinId ??
              r.providerId,

            providerId:
              r.providerId ??
              r.medecinId ??
              r.doctorId,

            patientId:
              r.patientId ??
              r.userId,

            reason:
              r.reason ?? null,

            // Ne jamais mettre "Doctor" ici
            providerDisplay:
              doctorName,

            providerName:
              doctorName,

            doctorName,

            medecinName:
              doctorName,

            specialtyDisplay:
              r.specialty ||
              r.specialtyDisplay ||
              "",

            specialty:
              r.specialty ||
              r.specialtyDisplay ||
              "",

            _display: {
              dateText,
              timeText,
            },
          };
        });
    },
    [dispatch]
  );

  /* ============================================================
     UPCOMING PATIENT APPOINTMENTS
  ============================================================ */

  const getUpcomingPatientAppointments =
    useCallback(
      (patientIdOpt) => {
        const patientId =
          patientIdOpt ??
          pickPatientId(authState);

        if (!patientId) {
          throw new Error(
            "Patient id not found."
          );
        }

        return once(
          `upcoming:${patientId}`,
          () =>
            _fetch(
              patientId,
              "upcoming"
            )
        );
      },
      [authState, _fetch, once]
    );

  /* ============================================================
     PAST PATIENT APPOINTMENTS
  ============================================================ */

  const getPastPatientAppointments =
    useCallback(
      (patientIdOpt) => {
        const patientId =
          patientIdOpt ??
          pickPatientId(authState);

        if (!patientId) {
          throw new Error(
            "Patient id not found."
          );
        }

        return once(
          `past:${patientId}`,
          () =>
            _fetch(
              patientId,
              "past"
            )
        );
      },
      [authState, _fetch, once]
    );

  /* ============================================================
     FETCH DOCTOR BY DATE
  ============================================================ */

  const getDoctorRendezvousByDate =
    useCallback(
      async (
        doctorIdOpt,
        targetDate
      ) => {
        const authUser =
          authState?.currentUser ||
          authState?.user ||
          {};

        const doctorUser =
          doctorState?.user || {};

        const doctorId =
          doctorIdOpt ||
          doctorUser?.doctorId ||
          doctorUser?.medecinId ||
          doctorUser?.externalId ||
          doctorUser?.uuid ||
          doctorUser?.pgId ||
          doctorUser?._id ||
          doctorUser?.id ||
          doctorUser?.userId ||
          authUser?.doctorId ||
          authUser?.medecinId ||
          authUser?.externalId ||
          authUser?.uuid ||
          authUser?.pgId ||
          authUser?._id ||
          authUser?.id ||
          authUser?.userId ||
          null;

        if (!doctorId) {
          throw new Error(
            "Doctor id not found."
          );
        }

        const cacheKey =
          `doctor:${doctorId}:${
            targetDate?.toISOString() ||
            "today"
          }`;

        return once(
          cacheKey,
          async () => {
            const raw = await dispatch(
              fetchRendezvousByMedecin({
                userId: doctorId,
                query: {},
              })
            ).unwrap();

            const date =
              targetDate || new Date();

            return arr(raw)
              .filter((r) => {
                const status =
                  String(
                    r?.status || ""
                  ).toLowerCase();

                return (
                  status === "confirmed" ||
                  status === "rescheduled"
                );
              })
              .filter(
                (r) =>
                  r?.startTime &&
                  sameDay(
                    new Date(
                      r.startTime
                    ),
                    date
                  )
              )
              .sort(
                (a, b) =>
                  new Date(
                    a.startTime
                  ) -
                  new Date(
                    b.startTime
                  )
              )
              .map((r) => {
                const {
                  dateText,
                  timeText,
                } = toDisplayDates(r);

                return {
                  id:
                    r.id ?? r._id,

                  status:
                    r.status,

                  startTime:
                    r.startTime,

                  endTime:
                    r.endTime,

                  medecinId:
                    r.medecinId ??
                    r.doctorId,

                  doctorId:
                    r.doctorId ??
                    r.medecinId,

                  medecinName:
                    cleanName(
                      r.medecinName ||
                        r.doctorName
                    ),

                  doctorName:
                    cleanName(
                      r.doctorName ||
                        r.medecinName
                    ),

                  patientId:
                    r.patientId ??
                    r.userId,

                  patientName:
                    r.patientName ||
                    r.userName ||
                    "Patient",

                  reason:
                    r.reason ?? null,

                  specialty:
                    r.specialty || "",

                  _display: {
                    dateText,
                    timeText,
                  },
                };
              });
          }
        );
      },
      [
        dispatch,
        authState,
        doctorState,
        once,
      ]
    );

  /* ============================================================
     CANCEL
  ============================================================ */

  const cancelAppointment =
    useCallback(
      async (
        appointment,
        opts = {}
      ) => {
        const {
          patientId: patientIdOpt,
          doctorId: doctorIdOpt,
          by: byOpt = "patient",
        } = opts || {};

        const rendezvousId =
          appointment?.id ??
          appointment?._id;

        if (!rendezvousId) {
          throw new Error(
            "rendezvousId missing"
          );
        }

        const by = byOpt;

        if (by === "patient") {
          const patientId =
            resolvePatientId(
              authState,
              appointment,
              patientIdOpt
            );

          if (!patientId) {
            throw new Error(
              "patientId (userId) is required"
            );
          }

          await dispatch(
            cancelRendezvous({
              userId: String(
                patientId
              ),
              rendezvousId: String(
                rendezvousId
              ),
              by: "patient",
            })
          ).unwrap();

          return true;
        }

        if (by === "doctor") {
          const doctorId =
            resolveDoctorId(
              authState,
              appointment,
              doctorIdOpt
            ) ||
            doctorState?.user?.doctorId ||
            doctorState?.user?.medecinId ||
            doctorState?.user?.externalId ||
            doctorState?.user?.id;

          if (!doctorId) {
            throw new Error(
              "doctorId (userId) is required"
            );
          }

          await dispatch(
            cancelRendezvous({
              userId: String(
                doctorId
              ),
              rendezvousId: String(
                rendezvousId
              ),
              by: "doctor",
            })
          ).unwrap();

          return true;
        }

        const patientIdFallback =
          resolvePatientId(
            authState,
            appointment,
            patientIdOpt
          );

        const doctorIdFallback =
          resolveDoctorId(
            authState,
            appointment,
            doctorIdOpt
          ) ||
          doctorState?.user?.doctorId ||
          doctorState?.user?.medecinId ||
          doctorState?.user?.externalId ||
          doctorState?.user?.id;

        const userId =
          patientIdFallback ||
          doctorIdFallback;

        if (!userId) {
          throw new Error(
            "userId is required"
          );
        }

        await dispatch(
          cancelRendezvous({
            userId: String(userId),
            rendezvousId: String(
              rendezvousId
            ),
            by: "patient",
          })
        ).unwrap();

        return true;
      },
      [
        dispatch,
        authState,
        doctorState,
      ]
    );

  /* ============================================================
     RESCHEDULE
  ============================================================ */

  const rescheduleAppointment =
    useCallback(
      async ({
        appointment,
        startTime,
        endTime,
        by = "patient",
      }) => {
        const rendezvousId =
          appointment?.id ??
          appointment?._id;

        if (!rendezvousId) {
          throw new Error(
            "rendezvousId missing"
          );
        }

        if (!startTime) {
          throw new Error(
            "startTime is required"
          );
        }

        if (!endTime) {
          throw new Error(
            "endTime is required"
          );
        }

        let userId = null;

        if (by === "patient") {
          userId =
            appointment?.patientId ||
            appointment?.externalPatientId ||
            appointment?.userId ||
            pickPatientId(authState);
        } else if (by === "doctor") {
          userId =
            appointment?.medecinId ||
            appointment?.doctorId ||
            doctorState?.user?.doctorId ||
            doctorState?.user?.medecinId ||
            doctorState?.user?.externalId ||
            doctorState?.user?.id ||
            pickDoctorId(authState);
        } else {
          userId =
            appointment?.patientId ||
            appointment?.externalPatientId ||
            appointment?.userId ||
            pickPatientId(authState) ||
            appointment?.medecinId ||
            appointment?.doctorId ||
            doctorState?.user?.doctorId ||
            doctorState?.user?.medecinId ||
            doctorState?.user?.externalId ||
            doctorState?.user?.id ||
            pickDoctorId(authState);
        }

        if (!userId) {
          throw new Error(
            "userId is required for reschedule"
          );
        }

        return dispatch(
          rescheduleRendezvous({
            userId: String(userId),
            rendezvousId: String(
              rendezvousId
            ),
            startTime,
            endTime,
          })
        ).unwrap();
      },
      [
        dispatch,
        authState,
        doctorState,
      ]
    );

  /* ============================================================
     RETURN
  ============================================================ */

  return {
    bookRendezvous,
    cancelAppointment,
    rescheduleAppointment,

    getUpcomingPatientAppointments,
    getPastPatientAppointments,
    getDoctorRendezvousByDate,

    getConfirmedDoctorIdsForPatient,
    getConfirmedPatientIdsForDoctor,

    creating,
    createError,
    lastCreated,

    rescheduling,
    rescheduleError,
  };
}